import type {
  FraudEscalationAction,
  FraudReviewDisposition,
  FraudReviewDonationItem,
  FraudReviewFindingItem,
  FraudReviewSeverity,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui';

export const RATIONALE_MAX_LENGTH = 2000;

/**
 * DM-5803 retired TINY_VELOCITY, so only these two reach the queue. Rendered
 * as sentences rather than reason codes: RAPID_RATE is a token, "entered in a
 * burst" is a finding, and a reviewer should not have to learn the vocabulary.
 */
const REASON_LABELS: Record<string, string> = {
  RAPID_RATE: 'Entered in a burst',
  DUPLICATE_PATTERN: 'Repeated identical gift',
};

/** Unknown codes fall through to the raw code rather than being hidden. */
export const reasonLabel = (code: string): string => REASON_LABELS[code] ?? code;

/**
 * DM-5803: the gap between when a gift was MADE and when it was ENTERED is
 * what separates a tax-season backfill from a burst of giving. Surfacing it
 * per donation is the single most useful thing the case view does.
 *
 * Returns null when there is nothing worth saying — no recorded gift date, or
 * a gap too small to be evidence of anything.
 */
export const entryGapLabel = (donation: FraudReviewDonationItem): string | null => {
  if (donation.donation_date === null) return null;
  const madeAt = new Date(donation.donation_date).getTime();
  const enteredAt = new Date(donation.created_at).getTime();
  if (Number.isNaN(madeAt) || Number.isNaN(enteredAt)) return null;
  const days = Math.round((enteredAt - madeAt) / 86_400_000);
  if (days < 2) return null;
  return `entered ${days} days later`;
};

/** DM-5805 bounds a suspension to a whole number of days in this range. */
export const SUSPENSION_MIN_DAYS = 1;
export const SUSPENSION_MAX_DAYS = 365;

export interface ResolutionDraft {
  disposition: FraudReviewDisposition | '';
  rationale: string;
  escalation: FraudEscalationAction | '';
  /** Raw field value; '' means the reviewer has not chosen a duration. */
  durationDays: string;
  /** Blocks resolution while a case's evidence is unavailable. */
  evidenceLoaded: boolean;
}

/**
 * A suspension duration is only meaningful for a SUSPEND escalation, and the
 * server rejects it anywhere else. Omitting it entirely makes the suspension
 * INDEFINITE, so the reviewer has to choose deliberately rather than by
 * leaving a field blank.
 */
export const isValidSuspensionDuration = (raw: string): boolean => {
  if (!/^\d+$/.test(raw)) return false;
  const days = Number(raw);
  return days >= SUSPENSION_MIN_DAYS && days <= SUSPENSION_MAX_DAYS;
};

/**
 * Mirrors the DM-5805 server contract, so the control disables for the same
 * reasons the API would answer 400: a rationale is required for EVERY
 * disposition, and an escalation target only when escalating.
 *
 * It adds one condition the server cannot enforce — the case's evidence must
 * have loaded. A failed detail request renders as an empty donation list, and
 * closing or escalating a case without having seen any of its evidence is
 * exactly the decision this queue exists to prevent.
 */
export const canSubmitResolution = (draft: ResolutionDraft): boolean => {
  if (!draft.evidenceLoaded) return false;
  if (draft.disposition === '') return false;
  if (draft.rationale.trim().length === 0) return false;
  if (draft.rationale.length > RATIONALE_MAX_LENGTH) return false;
  if (draft.disposition !== 'ESCALATE') return true;
  if (draft.escalation === '') return false;
  if (draft.escalation === 'SUSPEND') return isValidSuspensionDuration(draft.durationDays);
  return true;
};

/**
 * DM-5802: severity is readable at a glance, without parsing any numbers.
 * The stripe colour and the chip say the same thing, and the chip spells it
 * out for anyone who cannot rely on colour.
 */
export const SEVERITY_PRESENTATION: Record<
  FraudReviewSeverity,
  { label: string; tone: ChipTone; stripe: string }
> = {
  HIGH: { label: 'High', tone: 'crit', stripe: 'bg-red-500' },
  MEDIUM: { label: 'Medium', tone: 'warn', stripe: 'bg-yellow-400' },
  LOW: { label: 'Low', tone: 'info', stripe: 'bg-primary' },
};

/**
 * Each rule keeps one tone everywhere it appears, matching the severity it
 * earns on its own (RAPID_RATE alone is MEDIUM, DUPLICATE_PATTERN alone LOW).
 */
const REASON_TONES: Record<string, ChipTone> = {
  RAPID_RATE: 'warn',
  DUPLICATE_PATTERN: 'info',
};

export const reasonTone = (code: string): ChipTone => REASON_TONES[code] ?? 'neutral';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;
const DAYS_PER_WEEK = 7;

const plural = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** "48 minutes", "3 hours", "2 days"; a same-minute burst reads as such. */
export const formatSpan = (minutes: number): string => {
  if (minutes < 1) return 'under a minute';
  if (minutes < MINUTES_PER_HOUR) return plural(minutes, 'minute');
  if (minutes < MINUTES_PER_DAY) return plural(Math.round(minutes / MINUTES_PER_HOUR), 'hour');
  return plural(Math.round(minutes / MINUTES_PER_DAY), 'day');
};

const clusterPhrase = (days: number): string =>
  days === DAYS_PER_WEEK ? 'in the same week' : `within ${plural(days, 'day')} of each other`;

const FINDING_SUBJECTS: Record<string, string> = {
  RAPID_RATE: 'gifts',
  DUPLICATE_PATTERN: 'identical gifts',
};

/**
 * DM-5802: the finding with the numbers that triggered it, e.g. "6 gifts in
 * 48 minutes, dated in the same week". An unknown rule still shows its count
 * rather than disappearing.
 */
export const findingSentence = (finding: FraudReviewFindingItem): string => {
  const subject = FINDING_SUBJECTS[finding.code] ?? `${finding.code} events`;
  return `${finding.count} ${subject} in ${formatSpan(finding.span_minutes)}, dated ${clusterPhrase(
    finding.cluster_days
  )}`;
};

/**
 * Codes flagged before evidence was captured have no finding; those still
 * show their plain-language label, so no reason is ever dropped.
 */
export const reasonsWithoutFindings = (
  reasons: readonly string[],
  findings: readonly FraudReviewFindingItem[]
): string[] => {
  const described = new Set(findings.map(finding => finding.code));
  return reasons.filter(reason => !described.has(reason));
};

/** The donor's name when there is one, otherwise the email, otherwise nothing. */
export const donorName = (donor: { display_name: string | null; email: string | null }) =>
  donor.display_name ?? donor.email;

export const formatAccountAge = (days: number): string => {
  if (days < 1) return 'created today';
  if (days < 365) return `${plural(days, 'day')} old`;
  const years = Math.floor(days / 365);
  return `${plural(years, 'year')} old`;
};

export const formatAmount = (cents: number | null): string => {
  if (cents === null) return '—';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const truncatedCaseNotice = (shown: number, total: number): string | null => {
  if (shown >= total) return null;
  return `Showing the ${shown.toLocaleString('en-US')} most recent of ${total.toLocaleString('en-US')} flagged donations.`;
};

export const formatDay = (iso: string | null): string => {
  if (iso === null) return 'not recorded';
  return new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' });
};

/**
 * DM-5802: whether a presigned receipt URL has lapsed. A missing or
 * unparseable expiry counts as lapsed, so a failed image always gets one
 * fresh URL rather than none.
 */
export const isReceiptUrlExpired = (expiresAt: string | null, now: number): boolean => {
  if (expiresAt === null) return true;
  const expiry = new Date(expiresAt).getTime();
  return Number.isNaN(expiry) || expiry <= now;
};
